import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../supabaseClient';

interface Organization {
  id: string;
  name: string;
}

const OrganizationSwitcher: React.FC = () => {
  const { isSuperAdmin, viewingOrganizationId, switchToOrganization, clearOrganizationView } = useAuth();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(false);

  // Get the display text for the current selection
  const getCurrentDisplayText = () => {
    if (!viewingOrganizationId) {
      return 'Platform Overview';
    }
    const org = organizations.find(o => o.id === viewingOrganizationId);
    return org ? org.name : 'Loading...';
  };

  useEffect(() => {
    const fetchOrganizations = async () => {
      if (!isSuperAdmin) return;

      setLoading(true);
      try {
        const { data, error } = await supabase
          .from('organizations')
          .select('id, name')
          .order('name');

        if (error) {
          console.error('Error fetching organizations:', error);
        } else {
          setOrganizations(data || []);
        }
      } catch (err) {
        console.error('Error fetching organizations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
  }, [isSuperAdmin]);

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="relative">
      <label htmlFor="org-switcher" className="sr-only">
        Switch Organization
      </label>
      <select
        id="org-switcher"
        value={viewingOrganizationId || ''}
        onChange={(e) => { 
          if (e.target.value) {
            switchToOrganization(e.target.value);
          } else {
            // Switch back to Platform Overview
            clearOrganizationView();
          }
        }}
        className="block w-full max-w-[160px] sm:max-w-[220px] pl-3 pr-10 py-2 text-base border-surface-border focus:outline-none focus:ring-accent-blue focus:border-accent-blue sm:text-sm rounded-lg truncate"
        disabled={loading}
      >
        <option value="">Platform Overview</option>
        {organizations.map((org) => (
          <option key={org.id} value={org.id}>
            {org.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default OrganizationSwitcher;
